import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Settings, ChevronLeft, ChevronRight, Moon, Coffee, Droplets, Scale, Dumbbell, Sun, Sparkles } from 'lucide-react';
import { WhatToEatModal } from '@/components/WhatToEatModal';
import { CoachPanel } from '@/components/CoachPanel';
import { UniversalWorkoutModal } from '@/components/UniversalWorkoutModal';
import { CelebrationOverlay } from '@/components/CelebrationOverlay';
import { FeatureTour } from '@/components/FeatureTour';
import { WaterTracker } from '@/components/WaterTracker';
import { RecipeBottomSheet } from '@/components/RecipeBottomSheet';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';

import { C, GREETINGS, SectionLabel, Pill } from '@/components/dashboard/dashboardConstants';
import { useDashboardData } from '@/components/dashboard/useDashboardData';
import { CalorieRing } from '@/components/dashboard/CalorieRing';
import { MealRow } from '@/components/dashboard/MealRow';
import { SuggestedMealCard } from '@/components/dashboard/SuggestedMealCard';
import { ProactiveCoachCard } from '@/components/dashboard/ProactiveCoachCard';
import { SleepLogModal } from '@/components/dashboard/SleepLogModal';
import { WeeklyWinsCard } from '@/components/dashboard/WeeklyWinsCard';
import { InspirationCard } from '@/components/dashboard/InspirationCard';
import GentleDashboard from '@/components/dashboard/GentleDashboard';
import { getUIConfig } from '@/config/uiConfig';
import { HealthCard, WaterFillIcon, RecoveryFillIcon, SleepFillIcon, WeightIcon, ActivityIcon, MoodIcon } from '@/components/dashboard/HealthCard';
import { useMoodData, MoodSubtitle, getMoodCardProps } from '@/components/dashboard/MoodEnergyCard';
import { WeighInModal } from '@/components/dashboard/WeighInModal';
import { WaterLogModal } from '@/components/dashboard/WaterLogModal';
import { MoodLogModal } from '@/components/dashboard/MoodLogModal';
import { NuriCompanion } from '@/components/NuriCompanion';

const API = '/api';

export default function Dashboard() {
  const { user } = useAuth();

  // If user is in Gentle Mode, render the Gentle Dashboard
  if (user?.experience_mode === 'gentle') {
    return <GentleDashboard />;
  }

  return <PerformanceDashboard />;
}

function PerformanceDashboard() {
  const data = useDashboardData();
  const {
    navigate, user, authHeaders, token, selectedDate, setSelectedDate,
    meals, loading, streak, macroGap,
    showWhatToEat, setShowWhatToEat,
    showCoachPanel, setShowCoachPanel,
    coachInitialMessage, setCoachInitialMessage,
    showExerciseModal, setShowExerciseModal,
    celebration, setCelebration,
    waterCups, latestWeight, todayExercise, lastSleep, setLastSleep,
    inspirationQuote, recoveryScore,
    showTour, completeTour,
    dateStr, isToday, timeOfDay, firstName,
    dailyTotals, targets, remaining, protRemaining,
    addDays, subDays,
  } = data;

  const [showSleepModal, setShowSleepModal] = useState(false);
  const [showWeighInModal, setShowWeighInModal] = useState(false);
  const [showWaterModal, setShowWaterModal] = useState(false);
  const [showMoodModal, setShowMoodModal] = useState(false);
  const [recipeSheet, setRecipeSheet] = useState({ open: false, search: '', category: '', title: '' });
  const handleRecipeSearch = ({ search, category, title }) => setRecipeSheet({ open: true, search, category, title });

  const { moodData, refetch: refetchMood } = useMoodData();

  // Expertise-aware UI config
  const uiConfig = getUIConfig(user?.experience_mode || 'progress', user?.expertise_level || 'intermediate');
  const isGentle = user?.experience_mode === 'gentle';
  const isGlp1 = user?.health_mode === 'glp1';

  // Nuri Companion state
  const hour = new Date().getHours();
  const nuriState = hour >= 23 || hour < 5 ? 'sleeping'
    : dailyTotals.calories >= targets.calories && dailyTotals.protein >= targets.protein ? 'excited'
    : meals.length >= 2 ? 'happy'
    : isGentle ? 'empathetic' : 'neutral';

  // GLP-1 swap logic for slot 6: shows GLP-1 if dose not logged today, else Mood & Energy
  const Glp1OrMoodSlot = () => {
    const [doseLogged, setDoseLogged] = React.useState(true); // Default to mood
    React.useEffect(() => {
      (async () => {
        try {
          const res = await axios.get(`${API}/glp1/dose-today`, authHeaders());
          setDoseLogged(res.data?.logged_today || false);
        } catch { setDoseLogged(false); }
      })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    if (!doseLogged) {
      return <HealthCard testId="glp1-card" icon={Sparkles} iconColor="#5A8C6A" title="GLP-1" subtitle="Track dose" onClick={() => navigate('/glp1')} />;
    }
    return (
      <HealthCard testId="mood-energy-card" icon={Sun} iconColor="#C67A5C" title="Mood & Energy" onClick={() => setShowMoodModal(true)}>
        <MoodSubtitle moodData={moodData} mode={user?.experience_mode} expertiseLevel={user?.expertise_level} />
      </HealthCard>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, paddingBottom: 100 }} data-testid="dashboard-page">

      {/* Header */}
      <header style={{ background: C.bg, position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 600, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 56 }}>
          <img src="/nuri-logo-mark.svg" alt="nuri" style={{ height: 32, cursor: 'pointer' }} onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
          <button data-testid="settings-btn" onClick={() => navigate('/settings')} style={{ padding: 8, background: 'none', border: 'none', cursor: 'pointer' }}>
            <Settings className="w-5 h-5" style={{ color: C.muted }} />
          </button>
        </div>
      </header>

      <main style={{ maxWidth: 600, margin: '0 auto', padding: '16px 24px 120px' }}>

        {/* Greeting + Nuri Companion */}
        <div className="nuri-reveal nuri-d0" style={{ marginBottom: isToday && inspirationQuote ? 0 : 32, display: 'flex', alignItems: 'center', gap: 12 }}>
          <NuriCompanion state={nuriState} size="sm" />
          <p data-testid="greeting" style={{ fontFamily: 'var(--font-serif)', fontSize: 36, margin: 0, color: C.text, fontWeight: 400 }}>
            {GREETINGS[timeOfDay]}, {firstName}
          </p>
        </div>

        {/* Inspiration — flows naturally from greeting */}
        {isToday && inspirationQuote && (
          <div className="nuri-reveal nuri-d0" style={{ padding: '6px 0 0', marginBottom: 32 }}>
            <p style={{ fontFamily: 'var(--font-serif)', fontSize: 16, lineHeight: 1.6, margin: 0, color: C.text, fontWeight: 400, fontStyle: 'italic' }}>
              {inspirationQuote.quote}
            </p>
          </div>
        )}

        {/* Date Navigator */}
        {!isToday && (
          <div className="nuri-reveal nuri-d1" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '10px 12px', marginBottom: 24 }}>
            <button data-testid="date-prev" onClick={() => setSelectedDate(subDays(selectedDate, 1))} style={{ padding: 6, background: 'none', border: 'none', cursor: 'pointer' }}>
              <ChevronLeft className="w-5 h-5" style={{ color: C.muted }} />
            </button>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 15, fontWeight: 500, color: C.text, margin: 0, fontFamily: 'var(--font-sans)' }}>
                {format(selectedDate, 'EEEE')}
              </p>
              <p style={{ fontSize: 12, color: C.muted, margin: '2px 0 0', fontFamily: 'var(--font-sans)' }}>{format(selectedDate, 'MMMM d, yyyy')}</p>
            </div>
            <button data-testid="date-next" onClick={() => setSelectedDate(addDays(selectedDate, 1))} style={{ padding: 6, background: 'none', border: 'none', cursor: 'pointer' }}>
              <ChevronRight className="w-5 h-5" style={{ color: C.muted }} />
            </button>
          </div>
        )}

        {/* 1. Macro Rings */}
        <div className="nuri-reveal nuri-d1" onClick={() => navigate('/daily-summary')} style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: uiConfig.dashboardDensity === 'compact' ? 20 : 32, cursor: 'pointer' }} data-testid="macro-summary">
          <CalorieRing consumed={dailyTotals.calories} target={targets.calories} />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: uiConfig.dashboardDensity === 'compact' ? 10 : 16 }}>
              {[
                { label: 'Protein', val: `${Math.round(dailyTotals.protein)}g`, color: C.green },
                { label: 'Carbs', val: `${Math.round(dailyTotals.carbs)}g`, color: C.gold },
                { label: 'Fat', val: `${Math.round(dailyTotals.fat)}g`, color: C.accent },
              ].map(m => (
                <div key={m.label} style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: 11, margin: 0, color: C.muted, fontFamily: 'var(--font-sans)' }}>{m.label}</p>
                  <p style={{ fontSize: uiConfig.dashboardDensity === 'compact' ? 18 : 20, margin: '2px 0 0', fontWeight: 500, color: m.color, fontFamily: 'var(--font-sans)' }}>{m.val}</p>
                </div>
              ))}
            </div>
            {/* Pro: Show advanced metrics (net cal, macro %) */}
            {uiConfig.showAdvancedMetrics && (
              <div style={{ display: 'flex', gap: 12, marginBottom: 8, fontSize: 10, color: C.muted, fontFamily: 'var(--font-sans)' }}>
                <span>Net: {Math.round(dailyTotals.calories - (todayExercise?.calories_burned || 0))} cal</span>
                <span>P: {targets.protein > 0 ? Math.round((dailyTotals.protein / targets.protein) * 100) : 0}%</span>
                <span>C: {targets.carbs > 0 ? Math.round((dailyTotals.carbs / targets.carbs) * 100) : 0}%</span>
                <span>F: {targets.fat > 0 ? Math.round((dailyTotals.fat / targets.fat) * 100) : 0}%</span>
              </div>
            )}
            <div style={{ height: 3, background: C.border, borderRadius: 2 }}>
              <div style={{ height: '100%', width: `${Math.min((dailyTotals.protein / targets.protein) * 100, 100)}%`, background: C.green, borderRadius: 2, transition: 'width 0.8s ease-out' }} />
            </div>
            <p style={{ fontSize: 12, margin: '8px 0 0', color: C.text, fontFamily: 'var(--font-sans)', fontWeight: 600 }}>
              {protRemaining > 0 ? `${protRemaining}g protein to go` : 'Protein target hit!'} · {remaining > 0 ? `${Math.round(remaining)} cal remaining` : `${Math.abs(Math.round(remaining))} cal over`}
            </p>
            {/* Beginner: Helper link */}
            {uiConfig.showHelperLinks && (
              <button onClick={() => navigate('/coach')} style={{ fontSize: 11, color: '#6B9E8A', background: 'none', border: 'none', cursor: 'pointer', marginTop: 4, fontFamily: 'var(--font-sans)', textDecoration: 'underline' }}>
                What do these numbers mean?
              </button>
            )}
          </div>
        </div>

        {/* 2. Today's Meals */}
        <div className="nuri-reveal nuri-d2" style={{ marginBottom: 24 }}>
          {loading ? (
            <div style={{ padding: '32px 0', textAlign: 'center' }}>
              <p style={{ fontSize: 13, color: C.muted }}>Loading...</p>
            </div>
          ) : meals.length === 0 ? (
            /* Empty state — no inline label needed */
            <div style={{ textAlign: 'center', padding: '0', position: 'relative', borderRadius: 20, overflow: 'hidden' }}>
              <div style={{ position: 'relative', width: '100%', height: 180, overflow: 'hidden', borderRadius: 20 }}>
                <img src="/food-images/poke_bowl.png" alt="Beautiful food" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'saturate(1.1) brightness(1.05) sepia(0.05)' }} />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(253,251,247,0.15) 0%, rgba(253,251,247,0.92) 60%)' }} />
                <div style={{ position: 'absolute', bottom: 16, left: 0, right: 0, textAlign: 'center' }}>
                  <p style={{ fontSize: 15, margin: '0 0 6px', fontWeight: 600, color: '#1A1A18', fontFamily: 'var(--font-sans)' }}>No meals logged yet</p>
                  <p style={{ fontSize: 13, margin: '0 0 16px', color: '#4A4540', fontFamily: 'var(--font-sans)' }}>Snap a photo to get started</p>
                  <button data-testid="empty-state-log-btn" onClick={() => navigate('/log-meal')} style={{
                    padding: '12px 28px', borderRadius: 28, background: C.black, color: '#FDFBF7',
                    border: 'none', fontFamily: 'var(--font-sans)', fontSize: 14, fontWeight: 500, cursor: 'pointer',
                  }}>Log a Meal</button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <p style={{ fontSize: 11, letterSpacing: '0.8px', color: '#9A8F87', fontFamily: 'var(--font-sans)', textTransform: 'uppercase', margin: '0 0 10px', fontWeight: 500 }}>Today's meals</p>
              {meals.map(meal => (
                <MealRow key={meal.meal_id} meal={meal} token={token} onClick={() => navigate(`/meal/${meal.meal_id}`)} />
              ))}
            </>
          )}
        </div>

        {/* 3. Your Next Move — single prioritized card (reminders + coaching merged) */}
        {isToday && (
          <ProactiveCoachCard
            meals={meals} dailyTotals={dailyTotals} targets={targets}
            waterCups={waterCups} todayExercise={todayExercise} lastSleep={lastSleep}
            navigate={navigate} onRecipeSearch={handleRecipeSearch}
            macroGap={macroGap} remaining={remaining} protRemaining={protRemaining}
          />
        )}

        {/* 4. Suggested Next Meal */}
        {isToday && remaining > 100 && (
          <SuggestedMealCard remaining={remaining} protRemaining={protRemaining} authHeaders={authHeaders} navigate={navigate} />
        )}

        {/* 5. Health Cards grid (3x2) */}
        <div className="nuri-reveal nuri-d4" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }} data-testid="health-cards-row-1">
          <HealthCard testId="water-card" icon={Droplets} iconColor="#4A90D9" title="Water"
            fillIcon={<WaterFillIcon pct={Math.min(100, (waterCups / 10) * 100)} size={60} />}
            subtitle={`${waterCups}/10 cups`} onClick={() => setShowWaterModal(true)} />
          <HealthCard testId="recovery-card" icon={Coffee} iconColor="#2EAA6F" title="Recovery"
            fillIcon={<RecoveryFillIcon pct={recoveryScore || 0} size={60} />}
            subtitle={recoveryScore != null ? `${recoveryScore}/100` : '\u2014'}
            subtitleColor={recoveryScore >= 75 ? '#5A8C6A' : recoveryScore >= 50 ? '#E5A84B' : '#C67A5C'}
            onClick={() => navigate('/recovery')} />
          <HealthCard testId="sleep-card" icon={Moon} iconColor="#8B7EC8" title="Sleep"
            fillIcon={<SleepFillIcon pct={lastSleep?.hours ? Math.min(100, (lastSleep.hours / 8) * 100) : 0} size={60} />}
            subtitle={lastSleep?.quality || "How'd you sleep?"} onClick={() => setShowSleepModal(true)} />
        </div>
        <div className="nuri-reveal nuri-d4" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }} data-testid="health-cards-row-2">
          <HealthCard testId="weight-card" icon={Scale} iconColor="#C67A5C" title="Weight"
            fillIcon={<WeightIcon size={60} />}
            subtitle={latestWeight?.weight ? `${latestWeight.weight} lbs` : 'Log weight'}
            onClick={() => setShowWeighInModal(true)} />
          <HealthCard testId="activity-card" icon={Dumbbell} iconColor="#4A8FE7" title="Activity"
            fillIcon={<ActivityIcon logged={!!todayExercise?.calories_burned} size={60} />}
            subtitle={todayExercise?.calories_burned ? `${todayExercise.calories_burned} cal` : 'Log exercise'}
            subtitleColor={todayExercise?.calories_burned ? '#4A8FE7' : undefined}
            onClick={() => setShowExerciseModal(true)} />
          {/* Slot 6: GLP-1 (if dose not logged) or Mood & Energy */}
          {isGlp1 ? (
            <Glp1OrMoodSlot />
          ) : (
            <HealthCard testId="mood-energy-card" icon={Sun} iconColor="#E8A54B" title="Mood & Energy"
              fillIcon={<MoodIcon emoji={moodData?.logged_today ? getMoodCardProps(moodData, user?.experience_mode, user?.expertise_level).emoji : null} size={60} />}
              onClick={() => setShowMoodModal(true)}>
              <MoodSubtitle moodData={moodData} mode={user?.experience_mode} expertiseLevel={user?.expertise_level} />
            </HealthCard>
          )}
        </div>

        {/* 6. Your Progress This Week */}
        {isToday && <WeeklyWinsCard streak={streak} meals={meals} navigate={navigate} showSubtitle={uiConfig.showCardSubtitles} />}

        <div style={{ height: 32 }} />
      </main>

      {/* Modals */}
      <WhatToEatModal isOpen={showWhatToEat} onClose={() => setShowWhatToEat(false)} />
      <CoachPanel isOpen={showCoachPanel} onClose={() => { setShowCoachPanel(false); setCoachInitialMessage(''); }} initialMessage={coachInitialMessage} />
      <UniversalWorkoutModal isOpen={showExerciseModal} onClose={() => setShowExerciseModal(false)} onLogged={() => {}} />
      {celebration && <CelebrationOverlay type={celebration} onDismiss={() => setCelebration(null)} />}
      {showTour && <FeatureTour onComplete={completeTour} />}
      {showSleepModal && <SleepLogModal onClose={() => setShowSleepModal(false)} onSaved={(s) => setLastSleep(s)} />}
      <WeighInModal open={showWeighInModal} onClose={() => setShowWeighInModal(false)} unitPref={user?.unit_preference} />
      <WaterLogModal open={showWaterModal} onClose={() => setShowWaterModal(false)} />
      <MoodLogModal open={showMoodModal} onClose={() => setShowMoodModal(false)} isGentle={isGentle} onSaved={refetchMood} />
      <RecipeBottomSheet
        isOpen={recipeSheet.open}
        onClose={() => setRecipeSheet(prev => ({ ...prev, open: false }))}
        searchQuery={recipeSheet.search}
        category={recipeSheet.category}
        title={recipeSheet.title}
      />
    </div>
  );
}
